import { useAppState } from 'AppProvider';
import useCartPrice from 'api/cartPricing/useCartPrice';
import { useMemo } from 'react';
import { ICartExperience } from 'types/cart';
import type { DiscountCode } from 'types/discount-code';
import {
  buildQuoteBookings,
  selectQuotedCartItems,
  ServerCartQuote,
  summariseQuote,
  TimeFrameListing,
} from 'utils/cart/serverCartQuote';

type UseServerCartQuoteResult = {
  isLoading: boolean;
  /** True while a quoted item's price is still unknown. */
  isQuotePending: boolean;
  /** The items priced by the server rather than locally; see `selectQuotedCartItems`. */
  quotedCartItemIds: string[];
  quote: ServerCartQuote | null;
};

/**
 * The continuous items of the cart, priced by the server.
 *
 * A continuously booked resource costs what its rate ladder, its nights or its hours say — figures the
 * plugin cannot work out from a tier price and a head count. So those items are quoted by the same
 * calculator that will charge them, one line per cart item, and everything else keeps its local price.
 * The guest's coupon is quoted with them, so a line shows what the charge will take off it; an amount
 * coupon brings every item it reaches along, so it is spent once across the cart.
 */
const useServerCartQuote = (
  cartItems: Record<string, ICartExperience> | false,
  listings: Record<string, TimeFrameListing>,
  /** False to price the items without the guest's coupon, as one item's own receipt does. */
  { withCoupon = true }: { withCoupon?: boolean } = {},
): UseServerCartQuoteResult => {
  const { discountCode: appliedCode }: DiscountCode = useAppState(
    (state: Record<string, unknown>) => state.discountCode,
  );
  const discountCode = withCoupon ? appliedCode : null;

  const quotedItems = useMemo(
    () => selectQuotedCartItems(cartItems || {}, listings, discountCode),
    [cartItems, listings, discountCode],
  );

  const bookings = useMemo(
    () => buildQuoteBookings(quotedItems),
    [quotedItems],
  );

  const { cart, error, isLoading } = useCartPrice({
    bookings,
    discountCode: discountCode?.code,
  });

  const quote = useMemo(() => (cart ? summariseQuote(cart) : null), [cart]);

  // A continuous item has no price the plugin can work out on its own, so until the server has
  // answered there is no total — not a total of zero. Callers must not read the cart as free while
  // this is true. A kept-over quote from an earlier cart can lack a line for an item added since.
  const isQuotePending =
    Object.keys(quotedItems).length > 0 &&
    (isLoading ||
      !!error ||
      !quote ||
      Object.keys(quotedItems).some((id) => !quote.lines[id]));

  return {
    isQuotePending,
    quotedCartItemIds: Object.keys(quotedItems),
    isLoading,
    quote,
  };
};

export default useServerCartQuote;
